import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Users, ChevronRight, ArrowLeft, CheckCircle2, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  LoadingState,
} from "@tabipla/ui";
import type { GroupResponseDto, CreateExpenseDto } from "@tabipla/shared";
import { useGroupMembers } from "../../application/hooks/useGroups";
import { useDefaultSession } from "../../application/hooks/useDefaultSession";
import { useCreateExpense } from "../../application/hooks/useExpenses";
import { useAuth } from "../../application/hooks/useAuth";
import { ExpenseForm } from "./expenses/ExpenseForm";

interface QuickExpenseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groups: GroupResponseDto[];
}

export function QuickExpenseDialog({ open, onOpenChange, groups }: QuickExpenseDialogProps) {
  const { t } = useTranslation();
  const { participant } = useAuth();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [lastAddedExpense, setLastAddedExpense] = useState<{
    amount: number;
    description: string;
  } | null>(null);
  const [formKey, setFormKey] = useState(0); // Key to force ExpenseForm remount

  // Get members for selected group
  const { data: members } = useGroupMembers(selectedGroupId ?? undefined);

  // Get/create default session for selected group
  const selectedGroup = groups.find((g) => g.id === selectedGroupId);
  const canEdit = selectedGroup?.myRole === "owner" || selectedGroup?.myRole === "editor";
  const { session, isLoading: sessionLoading } = useDefaultSession(
    selectedGroupId ?? undefined,
    canEdit
  );

  // Create expense mutation
  const createExpenseMutation = useCreateExpense(selectedGroupId ?? "", session?.id ?? "");

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedGroupId(null);
      setShowExpenseForm(false);
      setShowSuccessScreen(false);
      setLastAddedExpense(null);
    }
  }, [open]);

  // Auto-select group if only one group exists
  useEffect(() => {
    if (open && groups.length === 1 && !selectedGroupId) {
      setSelectedGroupId(groups[0].id);
    }
  }, [open, groups, selectedGroupId]);

  // Automatically show expense form when session is ready
  useEffect(() => {
    if (selectedGroupId && session && members && members.length > 0) {
      setShowExpenseForm(true);
    }
  }, [selectedGroupId, session, members]);

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
  };

  const handleBack = () => {
    if (showSuccessScreen) {
      setShowSuccessScreen(false);
      setShowExpenseForm(true);
    } else {
      setSelectedGroupId(null);
      setShowExpenseForm(false);
    }
  };

  const handleExpenseSubmit = async (expense: CreateExpenseDto) => {
    try {
      await createExpenseMutation.mutateAsync(expense);
      setLastAddedExpense({ amount: expense.amount, description: expense.description });
      setShowExpenseForm(false);
      setShowSuccessScreen(true);
      toast.success(t("expenses.createSuccess", "経費を追加しました"));
    } catch {
      toast.error(t("expenses.createError", "経費の追加に失敗しました"));
    }
  };

  const handleAddAnother = useCallback(() => {
    setShowSuccessScreen(false);
    setFormKey((prev) => prev + 1); // Force ExpenseForm remount to reset state
    setShowExpenseForm(true);
  }, []);

  const handleClose = () => {
    onOpenChange(false);
  };

  // Show success screen with option to add another expense
  if (showSuccessScreen && selectedGroupId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-h-[80vh]">
          <div className="flex flex-col items-center py-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold">
              {t("expenses.addedSuccess", "追加しました！")}
            </h3>
            {lastAddedExpense && (
              <p className="mb-6 text-muted-foreground">
                {lastAddedExpense.description} - ¥{lastAddedExpense.amount.toLocaleString()}
              </p>
            )}
            <div className="flex w-full flex-col gap-2">
              <Button onClick={handleAddAnother} className="w-full" size="lg">
                <Plus className="mr-2 h-5 w-5" />
                {t("expenses.addAnother", "もう1件追加")}
              </Button>
              <Button variant="outline" onClick={handleClose} className="w-full" size="lg">
                <X className="mr-2 h-5 w-5" />
                {t("common.close", "閉じる")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show expense form directly (using the shared ExpenseForm component)
  if (showExpenseForm && session && members && selectedGroupId) {
    return (
      <ExpenseForm
        key={formKey}
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            onOpenChange(false);
          }
        }}
        members={members}
        currentUserId={participant?.id ?? ""}
        sessionId={session.id}
        editingExpense={null}
        onSubmit={(expense) => handleExpenseSubmit(expense as CreateExpenseDto)}
        isSubmitting={createExpenseMutation.isPending}
        quickMode
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        className="max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          {selectedGroupId ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DialogTitle>{t("expenses.addExpense", "経費を追加")}</DialogTitle>
            </div>
          ) : (
            <DialogTitle>{t("quickExpense.selectGroup", "わりかんを選択")}</DialogTitle>
          )}
        </DialogHeader>

        {/* Loading state while fetching session */}
        {selectedGroupId && sessionLoading ? (
          <LoadingState message={t("sessions.loading", "読み込み中...")} />
        ) : !selectedGroupId ? (
          /* Group selection */
          <div className="space-y-2">
            {groups.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                {t("groups.noGroups", "まだわりかんがありません")}
              </p>
            ) : (
              groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleGroupSelect(group.id)}
                  className="flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors hover:bg-accent"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                    <Users className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-semibold">{group.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {group.memberCount} {t("groups.members", "人")}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                </button>
              ))
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
